import React, { useState, useMemo, useCallback } from "react";
import { Calendar, Button, Badge, Tooltip, Tag, Empty, Segmented } from "antd";
import { LeftOutlined, RightOutlined, HomeOutlined, AppstoreOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isoWeek from "dayjs/plugin/isoWeek";
import updateLocale from "dayjs/plugin/updateLocale";
import { useTranslation } from "react-i18next";
import { dayStatuses } from "../../core/utils/weekPlanUtils";
import ApplicationDetailsModal from "./ApplicationDetailsModal";
import styles from "./WorkStatusCalendar.module.css";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);
// German convention: weeks start on Monday. The Ant <Calendar> reads the week
// start from the active dayjs locale (default "en" starts Sunday), so force it.
dayjs.extend(updateLocale);
dayjs.updateLocale("en", { weekStart: 1 });

// Status colors matching existing statusMetaMap
const statusColors = {
  0: "default",   // Pending
  1: "processing", // Requested (blue)
  2: "success",   // Approved (green)
  3: "error",     // Rejected (red)
  4: "warning",   // Cancelled (orange)
};

// Day status colors for application type
const dayStatusColors = {
  1: "#efdbff", // Holiday - light purple
  3: "#ffccc7", // Sick - light coral
  7: "#ffd6e7", // ChildSick - light pink
};

const WorkStatusCalendar = ({
  applications = [],
  isAdmin = false,
  onApprove,
  onReject,
  loading = false
}) => {
  const { t } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [currentYear, setCurrentYear] = useState(dayjs().year());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [viewMode, setViewMode] = useState("month"); // "month" or "year"

  // Group applications by date for efficient lookup
  const applicationsByDate = useMemo(() => {
    const grouped = {};

    applications.forEach(app => {
      const startDate = dayjs(app.from);
      const endDate = dayjs(app.to);

      // Iterate through each day in the date range
      let current = startDate;
      while (current.isSameOrBefore(endDate, 'day')) {
        const dateKey = current.format('YYYY-MM-DD');
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(app);
        current = current.add(1, 'day');
      }
    });

    return grouped;
  }, [applications]);

  // Navigation handlers
  const handlePrevMonth = useCallback(() => {
    setAnimating(true);
    setTimeout(() => {
      setCurrentMonth(prev => prev.subtract(1, "month"));
      setAnimating(false);
    }, 150);
  }, []);

  const handleNextMonth = useCallback(() => {
    setAnimating(true);
    setTimeout(() => {
      setCurrentMonth(prev => prev.add(1, "month"));
      setAnimating(false);
    }, 150);
  }, []);

  const handleToday = useCallback(() => {
    setAnimating(true);
    setTimeout(() => {
      setCurrentMonth(dayjs());
      setCurrentYear(dayjs().year());
      setAnimating(false);
    }, 150);
  }, []);

  // Year view navigation handlers
  const handlePrevYear = useCallback(() => {
    setAnimating(true);
    setTimeout(() => {
      setCurrentYear(prev => prev - 1);
      setAnimating(false);
    }, 150);
  }, []);

  const handleNextYear = useCallback(() => {
    setAnimating(true);
    setTimeout(() => {
      setCurrentYear(prev => prev + 1);
      setAnimating(false);
    }, 150);
  }, []);

  // Generate all days for a given month (for year view mini-calendars)
  const getMonthDays = useCallback((year, month) => {
    const firstDay = dayjs().year(year).month(month).startOf('month');
    const lastDay = dayjs().year(year).month(month).endOf('month');
    const startOfWeek = firstDay.startOf('isoWeek');
    const endOfWeek = lastDay.endOf('isoWeek');

    const days = [];
    let current = startOfWeek;

    while (current.isSameOrBefore(endOfWeek, 'day')) {
      days.push(current);
      current = current.add(1, 'day');
    }

    return days;
  }, []);

  // Get applications count and status for a specific date (for year view dots)
  const getDateApplicationInfo = useCallback((date) => {
    const dateKey = date.format('YYYY-MM-DD');
    const appsForDate = applicationsByDate[dateKey] || [];

    if (appsForDate.length === 0) return null;

    // Determine the most important status to show
    const hasRequested = appsForDate.some(app => app.statusCode === 1);
    const hasApproved = appsForDate.some(app => app.statusCode === 2);
    const hasRejected = appsForDate.some(app => app.statusCode === 3);

    let primaryStatus = 'default';
    if (hasRequested) primaryStatus = 'processing';
    else if (hasApproved) primaryStatus = 'success';
    else if (hasRejected) primaryStatus = 'error';

    return {
      count: appsForDate.length,
      status: primaryStatus,
      apps: appsForDate
    };
  }, [applicationsByDate]);

  // Handle date cell click
  const handleDateClick = useCallback((date) => {
    const dateKey = date.format('YYYY-MM-DD');
    const appsForDate = applicationsByDate[dateKey] || [];

    if (appsForDate.length > 0) {
      setSelectedDate(date);
      setIsModalVisible(true);
    }
  }, [applicationsByDate]);

  // Build tooltip string for a single application (shared across month/year views)
  const buildAppTooltipLine = useCallback((app) => {
    const reason = dayStatuses[app.reasonCode] || app.reason;
    const holidayPart = app.reasonCode === 1 && app.holidayDays != null
      ? ` (${app.holidayDays} ${t('days_name')})`
      : '';
    const commentPart = app.comment
      ? ` — ${app.comment.length > 40 ? app.comment.substring(0, 40) + '...' : app.comment}`
      : '';
    return { reason, holidayPart, commentPart };
  }, [t]);

  // Custom date cell renderer
  const dateFullCellRender = useCallback((date) => {
    const dateKey = date.format('YYYY-MM-DD');
    const appsForDate = applicationsByDate[dateKey] || [];
    const isToday = date.isSame(dayjs(), 'day');
    const isCurrentMonth = date.month() === currentMonth.month();
    const hasApps = appsForDate.length > 0;

    return (
      <div
        className={`${styles.dateCell} ${isToday ? styles.today : ''} ${!isCurrentMonth ? styles.otherMonth : ''} ${hasApps ? styles.hasApplications : ''}`}
        onClick={() => handleDateClick(date)}
      >
        <div className={styles.dateNumber}>
          {date.date()}
        </div>

        {hasApps && (
          <div className={styles.applicationBadges}>
            {isAdmin ? (
              // Admin view: show employee initials with status
              <>
                {appsForDate.slice(0, 2).map((app, index) => {
                  const initials = app.userName
                    ? app.userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                    : '??';
                  return (
                    <Tooltip
                      key={`${app.key}-${index}`}
                      title={(() => { const { reason, holidayPart, commentPart } = buildAppTooltipLine(app); return `${app.userName} - ${reason}${holidayPart}${commentPart}`; })()}
                    >
                      <Badge
                        status={statusColors[app.statusCode] || 'default'}
                        text={
                          <span className={styles.employeeInitial}>
                            {initials}
                          </span>
                        }
                      />
                    </Tooltip>
                  );
                })}
                {appsForDate.length > 2 && (
                  <span className={styles.moreIndicator}>
                    +{appsForDate.length - 2}
                  </span>
                )}
              </>
            ) : (
              // Employee view: show status badge with type
              <>
                {appsForDate.slice(0, 1).map((app, index) => (
                  <Tooltip
                    key={`${app.key}-${index}`}
                    title={(() => { const { reason, holidayPart, commentPart } = buildAppTooltipLine(app); return `${reason} - ${app.status}${holidayPart}${commentPart}`; })()}
                  >
                    <div className={styles.employeeBadge}>
                      <Badge status={statusColors[app.statusCode] || 'default'} />
                      <span className={styles.appType}>
                        {t(`reasons.${(dayStatuses[app.reasonCode] || 'holiday').toLowerCase()}`) || dayStatuses[app.reasonCode]}
                      </span>
                    </div>
                  </Tooltip>
                ))}
                {appsForDate.length > 1 && (
                  <span className={styles.moreIndicator}>
                    +{appsForDate.length - 1}
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }, [applicationsByDate, currentMonth, isAdmin, handleDateClick, buildAppTooltipLine, t]);

  // Get applications for selected date (for modal)
  const selectedDateApplications = useMemo(() => {
    if (!selectedDate) return [];
    const dateKey = selectedDate.format('YYYY-MM-DD');
    return applicationsByDate[dateKey] || [];
  }, [selectedDate, applicationsByDate]);

  // Build rich tooltip content for year view
  const buildTooltipContent = (appInfo, date) => {
    if (!appInfo) return null;

    return (
      <div className={styles.tooltipContent}>
        <div className={styles.tooltipDate}>{date.format('DD.MM.YYYY')}</div>
        <div className={styles.tooltipList}>
          {appInfo.apps.slice(0, 5).map((app, idx) => {
            const statusColor = statusColors[app.statusCode] || 'default';
            const reasonName = dayStatuses[app.reasonCode] || app.reason;
            const isHolidayApp = app.reasonCode === 1;
            return (
              <div key={idx} className={styles.tooltipItem}>
                <Badge status={statusColor} />
                <span className={styles.tooltipName}>{app.userName || t('unknownUser')}</span>
                <span className={styles.tooltipReason}>({t(`reasons.${reasonName?.toLowerCase()}`) || reasonName})</span>
                {isHolidayApp && app.holidayDays != null && (
                  <span className={styles.tooltipReason}> — {app.holidayDays} {t('days_name')}</span>
                )}
                {app.comment && (
                  <div className={styles.tooltipReason} style={{ fontStyle: 'italic', marginTop: 2 }}>
                    {app.comment.length > 40 ? app.comment.substring(0, 40) + '...' : app.comment}
                  </div>
                )}
              </div>
            );
          })}
          {appInfo.apps.length > 5 && (
            <div className={styles.tooltipMore}>+{appInfo.apps.length - 5} {t('more')}</div>
          )}
        </div>
        <div className={styles.tooltipHint}>{t('clickForDetails')}</div>
      </div>
    );
  };

  // Render mini-calendar for year view
  const renderMiniMonth = (monthIndex) => {
    const days = getMonthDays(currentYear, monthIndex);
    const monthDate = dayjs().year(currentYear).month(monthIndex);
    const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

    return (
      <div key={monthIndex} className={styles.miniMonth}>
        <div className={styles.miniMonthHeader}>
          {monthDate.format('MMMM')}
        </div>
        <div className={styles.miniMonthGrid}>
          {/* Weekday headers */}
          {weekDays.map((day, idx) => (
            <div key={`header-${idx}`} className={styles.miniWeekdayHeader}>
              {day}
            </div>
          ))}
          {/* Days */}
          {days.map((day, idx) => {
            const isCurrentMonth = day.month() === monthIndex;
            const isToday = day.isSame(dayjs(), 'day');
            const appInfo = getDateApplicationInfo(day);
            const hasApps = appInfo !== null;

            // Get unique employees for this day (for showing multiple dots)
            const uniqueEmployees = hasApps
              ? [...new Map(appInfo.apps.map(app => [app.userName, app])).values()].slice(0, 3)
              : [];

            return (
              <Tooltip
                key={idx}
                title={hasApps ? buildTooltipContent(appInfo, day) : null}
                placement="top"
                overlayClassName={styles.yearViewTooltip}
              >
                <div
                  className={`${styles.miniDay} ${!isCurrentMonth ? styles.miniDayOtherMonth : ''} ${isToday ? styles.miniDayToday : ''} ${hasApps ? styles.miniDayHasApps : ''}`}
                  onClick={() => {
                    if (hasApps) {
                      setSelectedDate(day);
                      setIsModalVisible(true);
                    }
                  }}
                >
                  <span className={styles.miniDayNumber}>{day.date()}</span>
                  {hasApps && (
                    <div className={styles.miniDayDots}>
                      {uniqueEmployees.map((app, dotIdx) => (
                        <span
                          key={dotIdx}
                          className={`${styles.miniDayDot} ${styles[`dot${statusColors[app.statusCode] || 'default'}`]}`}
                        />
                      ))}
                      {appInfo.apps.length > 3 && (
                        <span className={styles.miniDayMoreDot}>+</span>
                      )}
                    </div>
                  )}
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.calendarContainer}>
      {/* Navigation Header */}
      <div className={styles.calendarHeader}>
        <div className={styles.navigationControls}>
          {viewMode === "month" ? (
            <>
              <Tooltip title={t("previousMonth")}>
                <Button
                  className={styles.navButton}
                  icon={<LeftOutlined />}
                  onClick={handlePrevMonth}
                  disabled={animating}
                />
              </Tooltip>

              <div className={styles.currentMonthDisplay}>
                <span className={styles.monthName}>
                  {currentMonth.format("MMMM YYYY")}
                </span>
              </div>

              <Tooltip title={t("today")}>
                <Button
                  className={styles.navButton}
                  icon={<HomeOutlined />}
                  onClick={handleToday}
                  disabled={animating}
                />
              </Tooltip>

              <Tooltip title={t("nextMonth")}>
                <Button
                  className={styles.navButton}
                  icon={<RightOutlined />}
                  onClick={handleNextMonth}
                  disabled={animating}
                />
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title={t("previousYear")}>
                <Button
                  className={styles.navButton}
                  icon={<LeftOutlined />}
                  onClick={handlePrevYear}
                  disabled={animating}
                />
              </Tooltip>

              <div className={styles.currentMonthDisplay}>
                <span className={styles.monthName}>
                  {currentYear}
                </span>
              </div>

              <Tooltip title={t("today")}>
                <Button
                  className={styles.navButton}
                  icon={<HomeOutlined />}
                  onClick={handleToday}
                  disabled={animating}
                />
              </Tooltip>

              <Tooltip title={t("nextYear")}>
                <Button
                  className={styles.navButton}
                  icon={<RightOutlined />}
                  onClick={handleNextYear}
                  disabled={animating}
                />
              </Tooltip>
            </>
          )}
        </div>

        {/* View Toggle & Legend */}
        <div className={styles.headerRight}>
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              {
                value: 'month',
                icon: <CalendarOutlined />,
                label: t('monthView'),
              },
              {
                value: 'year',
                icon: <AppstoreOutlined />,
                label: t('yearView'),
              },
            ]}
            className={styles.viewToggle}
          />
          <div className={styles.legend}>
            <Tag color="success">{t("Approved")}</Tag>
            <Tag color="processing">{t("Requested")}</Tag>
            <Tag color="error">{t("Rejected")}</Tag>
            <Tag color="warning">{t("Cancelled")}</Tag>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className={`${styles.calendarWrapper} ${animating ? styles.animating : ''}`}>
        {applications.length === 0 && !loading ? (
          <Empty
            description={t("noApplications") || "No applications found"}
            className={styles.emptyState}
          />
        ) : viewMode === "month" ? (
          <Calendar
            fullscreen={true}
            value={currentMonth}
            onPanelChange={(date) => setCurrentMonth(date)}
            fullCellRender={dateFullCellRender}
            headerRender={() => null}
          />
        ) : (
          <div className={styles.yearViewGrid}>
            {Array.from({ length: 12 }, (_, i) => renderMiniMonth(i))}
          </div>
        )}
      </div>

      {/* Application Details Modal */}
      <ApplicationDetailsModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        date={selectedDate}
        applications={selectedDateApplications}
        isAdmin={isAdmin}
        onApprove={onApprove}
        onReject={onReject}
      />
    </div>
  );
};

export default WorkStatusCalendar;
